import React, { useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Alert,
  TextField,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  Mic as MicIcon,
} from '@mui/icons-material';
import { gradients, surface } from '../theme';
import TranscriptionService from '../services/TranscriptionService';
import AuthService from '../services/AuthService';
import { Navigate } from 'react-router-dom';

function TranscriptionPage() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    percent: 0,
    loaded: 0,
    total: 0,
    speed: 0,
  });
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [assContent, setAssContent] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [copied, setCopied] = useState(false);

  // Refs for speed calculation
  const uploadStartTimeRef = useRef(null);
  const lastLoadedRef = useRef(0);
  const lastTimeRef = useRef(null);
  const recentSpeedsRef = useRef([]);

  // Redirect if user doesn't have permission
  if (!AuthService.hasRole(['editor', 'admin', 'superadmin', 'uploader', 'ugcCreator'])) {
    return <Navigate to='/' replace />;
  }

  // Helper function to format time remaining
  const formatTimeRemaining = (seconds) => {
    if (!seconds || seconds === Infinity || isNaN(seconds)) {
      return 'Calculating...';
    }
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    if (minutes < 60) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type (audio files)
      const validTypes = [
        'audio/mpeg',
        'audio/mp3',
        'audio/wav',
        'audio/wave',
        'audio/x-wav',
        'audio/mp4',
        'audio/m4a',
        'audio/ogg',
        'audio/webm',
        'audio/flac',
        'audio/aac',
      ];

      if (
        !validTypes.includes(file.type) &&
        !file.name.match(/\.(mp3|wav|m4a|ogg|webm|flac|aac)$/i)
      ) {
        setError(
          'Please select a valid audio file (MP3, WAV, M4A, OGG, WEBM, FLAC, or AAC)'
        );
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setError(null);
      setSuccess(null);
      setAssContent(null);
      setCopied(false);
      setUploadCompleted(false);
      setUploadProgress({ percent: 0, loaded: 0, total: file.size, speed: 0 });
    }
  };

  const handleUploadAndTranscribe = async () => {
    if (!selectedFile) {
      setError('Please select an audio file');
      return;
    }

    setError(null);
    setSuccess(null);
    setAssContent(null);
    setUploading(true);
    setUploadCompleted(false);
    setUploadProgress({
      percent: 0,
      loaded: 0,
      total: selectedFile.size,
      speed: 0,
    });

    // Initialize speed calculation refs
    uploadStartTimeRef.current = Date.now();
    lastLoadedRef.current = 0;
    lastTimeRef.current = uploadStartTimeRef.current;
    recentSpeedsRef.current = [];
    const MAX_SPEED_SAMPLES = 5;

    try {
      // Step 1: Upload audio file
      const { objectName } = await TranscriptionService.uploadAudio(
        selectedFile,
        (progress) => {
          const currentTime = Date.now();
          const elapsedTime = (currentTime - lastTimeRef.current) / 1000; // Convert to seconds
          const currentLoaded = progress.loaded;

          // Calculate instantaneous speed (MB/sec) based on recent progress
          let instantaneousSpeed = 0;
          if (elapsedTime > 0 && lastTimeRef.current) {
            const bytesUploaded = currentLoaded - lastLoadedRef.current;
            const speedBytesPerSec = bytesUploaded / elapsedTime;
            instantaneousSpeed = speedBytesPerSec / (1024 * 1024); // Convert to MB/sec

            // Add to recent speeds array (keep only last 5)
            recentSpeedsRef.current.push(instantaneousSpeed);
            if (recentSpeedsRef.current.length > MAX_SPEED_SAMPLES) {
              recentSpeedsRef.current.shift(); // Remove oldest speed
            }
          }

          // Calculate average speed from last 5 measurements
          let averageSpeed = 0;
          if (recentSpeedsRef.current.length > 0) {
            const sum = recentSpeedsRef.current.reduce(
              (acc, speed) => acc + speed,
              0
            );
            averageSpeed = sum / recentSpeedsRef.current.length;
          }

          setUploadProgress({
            percent: progress.percent,
            loaded: progress.loaded,
            total: progress.total,
            speed: averageSpeed,
          });

          lastLoadedRef.current = currentLoaded;
          lastTimeRef.current = currentTime;
        }
      );

      setUploadCompleted(true);
      setUploading(false);
      setTranscribing(true);

      // Step 2: Transcribe audio
      const result = await TranscriptionService.transcribeAudio(objectName);

      setAssContent(result.ass);
      setSuccess('Audio transcribed successfully!');
      setTranscribing(false);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Failed to transcribe audio');
      setUploading(false);
      setTranscribing(false);
      setUploadCompleted(false);
    }
  };

  const handleDownloadASS = () => {
    if (!assContent) return;

    const blob = new Blob([assContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = selectedFile.name.replace(/\.[^/.]+$/, '') + '.ass';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopyASS = async () => {
    if (!assContent) return;

    try {
      await navigator.clipboard.writeText(assContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setError('Failed to copy to clipboard');
    }
  };

  return (
    <Box>
      <Card
        sx={{
          mb: 4,
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          background: surface.glass,
          backdropFilter: 'blur(10px)',
          border: surface.glassBorder,
          transition: 'box-shadow 0.3s ease',
          '&:hover': {
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                background: gradients.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
              }}
            >
              <MicIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography
                variant='h4'
                component='h2'
                gutterBottom
                sx={{ fontWeight: 700, mb: 0.5 }}
              >
                Audio Transcription
              </Typography>
              <Typography variant='body1' color='text.secondary'>
                Upload and transcribe audio files to generate ASS subtitles
              </Typography>
            </Box>
          </Box>

          {error && (
            <Alert
              severity='error'
              sx={{
                mb: 3,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(211, 47, 47, 0.15)',
              }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              severity='success'
              sx={{
                mb: 3,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(46, 125, 50, 0.15)',
              }}
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          )}

          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 2,
              background:
                'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
              border: '2px dashed rgba(102, 126, 234, 0.3)',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'rgba(102, 126, 234, 0.5)',
                background:
                  'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
              },
            }}
          >
            <input
              accept='audio/*,.mp3,.wav,.m4a,.ogg,.webm,.flac,.aac'
              style={{ display: 'none' }}
              id='audio-file-input'
              type='file'
              onChange={handleFileSelect}
              disabled={uploading || transcribing}
            />
            <label htmlFor='audio-file-input'>
              <Button
                variant='outlined'
                component='span'
                startIcon={<CloudUploadIcon />}
                disabled={uploading || transcribing}
                sx={{
                  mr: 2,
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    transform: 'scale(1.05)',
                  },
                }}
              >
                Select Audio File
              </Button>
            </label>
            {selectedFile && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant='body2'
                  sx={{
                    fontWeight: 600,
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    component='span'
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: gradients.primary,
                      display: 'inline-block',
                    }}
                  />
                  {selectedFile.name}
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mt: 0.5, ml: 2 }}
                >
                  Size: {TranscriptionService.formatFileSize(selectedFile.size)}
                </Typography>
              </Box>
            )}
          </Box>

          {(uploading || transcribing) && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant='body1'
                color='text.secondary'
                sx={{ fontWeight: 600, mb: 2 }}
              >
                {uploading
                  ? 'Uploading audio file...'
                  : 'Transcribing audio...'}
              </Typography>

              {uploading && (
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant='body2'
                      color={
                        uploadCompleted ? 'success.main' : 'text.secondary'
                      }
                      sx={{
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      {uploadCompleted && (
                        <CheckCircleIcon
                          sx={{ fontSize: 18, color: 'success.main' }}
                        />
                      )}
                      {selectedFile.name}
                    </Typography>
                    <Typography
                      variant='body2'
                      color={uploadCompleted ? 'success.main' : 'primary.main'}
                      sx={{ fontWeight: 700 }}
                    >
                      {uploadCompleted
                        ? 'Completed'
                        : `${uploadProgress.percent}%`}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant='determinate'
                    value={uploadCompleted ? 100 : uploadProgress.percent}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      background: 'rgba(102, 126, 234, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        background: uploadCompleted
                          ? 'linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)'
                          : gradients.primary90,
                      },
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 3,
                      mt: 1.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    {!uploadCompleted && (
                      <>
                        <Box>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ mb: 0.5 }}
                          >
                            Speed
                          </Typography>
                          <Typography
                            variant='body2'
                            color='primary.main'
                            sx={{ fontWeight: 600 }}
                          >
                            {uploadProgress.speed > 0
                              ? `${uploadProgress.speed.toFixed(2)} MB/sec`
                              : 'Calculating...'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ mb: 0.5 }}
                          >
                            Progress
                          </Typography>
                          <Typography
                            variant='body2'
                            color='primary.main'
                            sx={{ fontWeight: 600 }}
                          >
                            {uploadProgress.total > 0
                              ? `${TranscriptionService.formatFileSize(
                                  uploadProgress.loaded
                                )} / ${TranscriptionService.formatFileSize(
                                  uploadProgress.total
                                )}`
                              : 'Calculating...'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ mb: 0.5 }}
                          >
                            Time Remaining
                          </Typography>
                          <Typography
                            variant='body2'
                            color='primary.main'
                            sx={{ fontWeight: 600 }}
                          >
                            {uploadProgress.speed > 0 &&
                            uploadProgress.total > 0
                              ? formatTimeRemaining(
                                  (uploadProgress.total -
                                    uploadProgress.loaded) /
                                    (uploadProgress.speed * 1024 * 1024)
                                )
                              : 'Calculating...'}
                          </Typography>
                        </Box>
                      </>
                    )}
                    {uploadCompleted && (
                      <Box sx={{ width: '100%' }}>
                        <Chip
                          icon={<CheckCircleIcon />}
                          label='Upload complete'
                          color='success'
                          size='small'
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {transcribing && (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      Transcribing audio...
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant='indeterminate'
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      background: 'rgba(102, 126, 234, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        background: gradients.primary90,
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          )}

          {assContent && (
            <Box sx={{ mt: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant='h6'>Transcription Result (ASS)</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                    <IconButton
                      onClick={handleCopyASS}
                      color={copied ? 'success' : 'default'}
                      size='small'
                    >
                      {copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title='Download ASS file'>
                    <IconButton
                      onClick={handleDownloadASS}
                      color='primary'
                      size='small'
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={15}
                value={assContent}
                variant='outlined'
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                  },
                }}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Box>
          )}

          <Button
            variant='contained'
            onClick={handleUploadAndTranscribe}
            disabled={!selectedFile || uploading || transcribing}
            startIcon={<MicIcon />}
            sx={{
              mt: 3,
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              background: gradients.primary,
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: gradients.primaryHover,
                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                transform: 'translateY(-2px)',
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)',
              },
            }}
          >
            {uploading || transcribing ? 'Processing...' : 'Transcribe'}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}

export default TranscriptionPage;
